import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Images } from "lucide-react";
import { api } from "@/lib/api";

export function usePublicAlbums(tipo) {
  const [albums, setAlbums] = useState(null);
  useEffect(() => {
    api
      .get("/public/albums", { params: { tipo } })
      .then((r) => setAlbums(r.data))
      .catch(() => setAlbums([]));
  }, [tipo]);
  return albums;
}

export function PublicAlbums({ albums, testId }) {
  const [openId, setOpenId] = useState(null);
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const openAlbum = albums.find((a) => a.id === openId);
  const fotoAtiva = openAlbum && openAlbum.fotos.length
    ? openAlbum.fotos[Math.min(active, openAlbum.fotos.length - 1)]
    : null;

  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
    setActive(0);
    setExpanded(false);
  };

  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {albums.map((album) => (
          <button
            key={album.id}
            type="button"
            onClick={() => toggle(album.id)}
            className="group border border-border bg-white text-left transition-colors hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid={`public-album-${album.id}`}
          >
            {album.capa_url ? (
              <div className="aspect-[16/10] overflow-hidden bg-zinc-100">
                <img
                  src={album.capa_url}
                  alt={`Capa do álbum ${album.titulo}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </div>
            ) : (
              <div className="flex aspect-[16/10] items-center justify-center bg-zinc-100 text-zinc-400">
                <Images size={28} />
              </div>
            )}
            <div className="flex items-start justify-between gap-2 p-3">
              <div className="min-w-0">
                {album.ano && (
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">{album.ano}</p>
                )}
                <p className="mt-1 text-sm font-semibold leading-snug">{album.titulo}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{album.fotos.length} foto(s)</p>
              </div>
              {openId === album.id ? (
                <ChevronUp size={16} className="mt-1 shrink-0 text-primary" />
              ) : (
                <ChevronDown size={16} className="mt-1 shrink-0 text-muted-foreground" />
              )}
            </div>
          </button>
        ))}
      </div>

      {openAlbum && fotoAtiva && (
        <div className="mt-6 border border-border bg-white p-4" data-testid={`public-album-photos-${openAlbum.id}`}>
          <div className="lg:flex lg:gap-5">
            <button
              type="button"
              onClick={() => setLightbox(fotoAtiva)}
              className="group block w-full flex-1 focus:outline-none focus:ring-2 focus:ring-primary"
              title="Clique para ampliar"
              data-testid="public-main-photo"
            >
              <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-zinc-100">
                <img
                  src={fotoAtiva.url}
                  alt={fotoAtiva.titulo || "Foto do álbum"}
                  className="max-h-full w-full object-contain"
                />
              </div>
            </button>
            <div
              className="mt-3 flex gap-2 overflow-x-auto lg:mt-0 lg:max-h-[420px] lg:w-44 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1"
              data-testid="public-thumbs"
            >
              {openAlbum.fotos.map((foto, idx) => (
                <button
                  key={foto.id}
                  type="button"
                  onClick={() => setActive(idx)}
                  className={`shrink-0 border transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                    idx === active ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/60"
                  }`}
                  data-testid={`public-photo-${foto.id}`}
                >
                  <img
                    src={foto.url}
                    alt={foto.titulo || `Foto ${idx + 1} do álbum`}
                    loading="lazy"
                    className="h-20 w-24 object-cover lg:h-24 lg:w-full"
                  />
                </button>
              ))}
            </div>
          </div>

          {(fotoAtiva.titulo || fotoAtiva.ano || fotoAtiva.legenda) && (
            <div className="mt-3" data-testid="public-main-caption">
              {fotoAtiva.ano && (
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">{fotoAtiva.ano} · </span>
              )}
              <span className="text-sm font-semibold">{fotoAtiva.titulo}</span>
              {fotoAtiva.legenda && <p className="mt-0.5 text-xs text-muted-foreground">{fotoAtiva.legenda}</p>}
            </div>
          )}

          {openAlbum.descricao && (
            <div className="mt-3 border-t border-border pt-3">
              <p
                className={`text-sm leading-relaxed text-muted-foreground ${expanded ? "" : "line-clamp-2"}`}
                data-testid="public-album-desc"
              >
                {openAlbum.descricao}
              </p>
              {openAlbum.descricao.length > 140 && (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="mt-1 text-xs font-semibold text-primary hover:underline"
                  data-testid="public-album-desc-toggle"
                >
                  {expanded ? "Ver menos" : "Ler descrição completa"}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightbox(null)}
          data-testid="public-album-lightbox"
        >
          <div className="max-h-full w-full max-w-3xl overflow-auto bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.titulo || "Foto ampliada"} className="max-h-[70vh] w-full object-contain" />
            <div className="mt-3 flex items-start justify-between gap-4">
              <div>
                {lightbox.ano && (
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">{lightbox.ano}</p>
                )}
                {lightbox.titulo && <p className="mt-1 font-heading text-lg font-bold">{lightbox.titulo}</p>}
                {lightbox.legenda && <p className="mt-1 text-sm text-muted-foreground">{lightbox.legenda}</p>}
              </div>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="shrink-0 border border-border px-3 py-1 text-sm transition-colors hover:border-primary"
                data-testid="public-album-lightbox-close"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
